import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { Observable } from 'rxjs/Observable';
import { Set } from 'immutable';
import { EventListStore } from '../service/event-list.store';
import { OnyxSummaryEvent } from '../models/event.summary';
import { MenuStore } from '../../menus/service/menu.store';
import { EnvironmentConfiguration } from '../../environment/services/environment.configuration';
import { EventSearchParametersStore } from '../service/event-search-parameters.store';
import { EventSearchFilter } from '../models/event-filter';
import { LogService } from '../../shared/log/log.service';
import { EventFilterType } from '../models/event-filter-type-enum';
import { EventFilterUtilsService } from '../service/event-filter-util-service';
import { FlashMessagesService } from '../../shared/flash-messages/service/flash.messages.service';
import { EventSearchFilterValidator } from '../../menus/event-search-filter/event-search-filter.validator';
import { FormGroup } from '@angular/forms';
import { EventSearchFormsStore } from '../service/event-search-forms.store';
import { EventSearchText } from '../models/event-search-text';
import { EventSearchParamsDesc } from '../models/event-search-params';
import { TranslateService } from '@ngx-translate/core';
import { Page } from '../../shared/models/page';
import { PagesizeType } from '../service/pagination/pagesize-type';
import { UserLocalStorageService } from '../../permission/services/user.local.storage.service';
import { MultiAgencyService } from 'app/shared/multi-agency/multi-agency.service';
import { MultiAgency } from 'app/shared/models/multi-agency';
import { EventStore } from '../service/event.store';
import { Subscription } from 'rxjs/Subscription';
import { BulkActionPopupComponent } from '../bulk-action/bulk-action-popup/bulk-action-popup.component';
import { BulkAction } from '../models/bulk-action';

@Component({
  selector: 'pioneer-event-list',
  templateUrl: './event-list.component.html'
})
export class EventListComponent implements OnInit, OnDestroy {

  public eventSearchTextForm: FormGroup;

  pagesizeType = PagesizeType.EVENTS;

  defaultAgency$: Observable<MultiAgency> = this.multiAgencyService.defaultAgency$;

  agencySubscription$: Subscription = new Subscription();

  @ViewChild(BulkActionPopupComponent)
  public bulkActionPopup: BulkActionPopupComponent;

  constructor(private eventListStore: EventListStore,
              private environment: EnvironmentConfiguration,
              private menuStore: MenuStore,
              private logger: LogService,
              private flashMessagesService: FlashMessagesService,
              private eventSearchParametersStore: EventSearchParametersStore,
              private eventFilterUtils: EventFilterUtilsService,
              private eventSearchFormsStore: EventSearchFormsStore,
              private translateService: TranslateService,
              private userLocalStorageService: UserLocalStorageService,
              private multiAgencyService: MultiAgencyService,
              private eventStore: EventStore) {
  }

  ngOnInit() {
    this.menuStore.setPageHeader('EVENTS.TITLE', false);
    this.eventSearchFormsStore.populateSearchTextForm('');
    this.eventSearchTextForm = this.eventSearchFormsStore.searchTextForm;

    this.eventStore.resetSelectedEvent();

    this.agencySubscription$ = this.defaultAgency$.subscribe(() => {
      this.eventListStore.loadEvents(0, this.userLocalStorageService.getPagesize(PagesizeType.EVENTS)).take(1).subscribe();
    });
  }

  get searchTextSet(): Observable<Set<string>> {
    return this.eventSearchParametersStore.searchTextSet;
  }

  get searchFilterSet(): Observable<Set<EventSearchFilter>> {
    return this.eventSearchParametersStore.searchFilterSet;
  }

  get eventCount(): Observable<number> {
    return this.eventListStore.totalEventCount;
  }

  get eventsPresent(): Observable<boolean> {
    return this.eventCount.map(eventCount => {
      return eventCount > 0;
    });
  }

  get maxEvents(): number {
    return this.environment.events.maxEvents;
  }

  resetSearch(): void {
    this.eventSearchParametersStore.reset(true);
    this.eventSearchFormsStore.populateSearchTextForm('');
  }

  addSearchText(models: any): void {
    this.logger.info(`addSearchText: ${JSON.stringify(models)}`);

    const eventSearchText = new EventSearchText(models);

    if (!eventSearchText.searchText) {
      return;
    }

    const filterErrors = this.eventSearchFormsStore.filterMenuErrorsSnapshot;

    if (filterErrors != null) {
      if (filterErrors[EventSearchFilterValidator.DATE_ERROR]) {
        this.flashMessagesService.error('MESSAGES.ERROR.INVALID_DATE_RANGE');
      }

      if (filterErrors[EventSearchFilterValidator.PAYMENT_STATUS_ERROR]) {
        this.flashMessagesService.error('MESSAGES.ERROR.NO_PAYMENT_STATUS_SELECTED');
      }

      return;
    }

    this.eventSearchFormsStore.searchFromSearchText(eventSearchText);
  }

  addSearchFilter(filterField: string, filterValue: string): void {
    this.eventSearchParametersStore.addSearchFilter(filterField, filterValue);
    this.eventSearchParametersStore.updateUrl();
  }

  removeSearchText(event: Event): void {
    const element: Element = <Element>(event.target || event.srcElement);
    const searchText = element.innerHTML.trim();
    this.eventSearchParametersStore.removeSearchText(searchText, true);
  }

  get isDefaultViewSelected(): Observable<boolean> {
    return this.eventSearchParametersStore.isDefaultViewSelected;
  }

  updateToDefaultURL(): void {
    this.eventSearchParametersStore.defaultViewUrl();
  }

  updateToFinancialURL(): void {
    this.eventSearchParametersStore.financialViewUrl();
  }

  removeSearchFilter(event: Event, filter: EventSearchFilter): void {
    this.eventSearchParametersStore.removeSearchFilter(filter, true);
    const element: Element = <Element>(event.target || event.srcElement);
    element.innerHTML.trim();
  }

  getTokenFilterFieldTranslationKey(filterField: string) {
    switch (filterField) {
      case EventFilterType.COMPLETED.toString():
      case EventFilterType.MISSING_AGENCY.toString():
      case EventFilterType.ARCHIVED.toString():
        return 'EVENTS.FILTER_LABEL.EVENT_STATUS';
      case EventFilterType.COMM_RETENTION.toString():
          return 'EVENTS.FILTER_LABEL.EVENT_STATUS';
      case EventFilterType.DELETED.toString():
        return 'EVENTS.FILTER_LABEL.EVENT_STATUS';
      default:
        return 'EVENTS.FILTER_LABEL.' + filterField;
    }
  }

  getTokenValue(filter: EventSearchFilter): string {
    return this.eventFilterUtils.getTokenDisplayString(filter);
  }

  exportEventExcel(): void {

    let filterDescriptions = Set([]);

    this.eventSearchParametersStore.eventSearchParametersSnapshot.filters.forEach((element: EventSearchFilter) => {
      filterDescriptions = filterDescriptions.add(
        this.translateService.instant(this.getTokenFilterFieldTranslationKey(element.filterField)) +
        ' ' +
        this.getTokenValue(element));
    });

    let eventSearchParamsDesc = new EventSearchParamsDesc({
      maxEvents: 0,
      searchText: this.eventSearchParametersStore.eventSearchParametersSnapshot.searchText,
      filters: this.eventSearchParametersStore.eventSearchParametersSnapshot.filters,
      sorts: this.eventSearchParametersStore.eventSearchParametersSnapshot.sorts,
      filterDescriptions: filterDescriptions
    });

    this.eventListStore.exportEventExcel(eventSearchParamsDesc).subscribe();
  }

  onRefreshList(page: number) {
    this.eventListStore.loadEvents(page, this.userLocalStorageService.getPagesize(PagesizeType.EVENTS)).take(1).subscribe();
  }

  get events(): Observable<Page<OnyxSummaryEvent>> {
    return this.eventListStore.events;
  }

  ngOnDestroy() {
    this.agencySubscription$.unsubscribe();
  }

  public get BulkAction() {
    return BulkAction;
  }

  openBulkActionModal(action: BulkAction) {
    this.bulkActionPopup.open(action);
  }

  closedBulkActionModal(event: any) {
    this.onRefreshList(0);
  }
}



// WEBPACK FOOTER //
// /var/lib/jenkins/jobs/GroupPay/jobs/QA2/jobs/QA-Pioneer-ALL-2-POC/workspace/pioneer-web/src/app/event/event-list/event-list.component.ts